/*
  CleanWhey — AJAX add-to-cart that opens the theme's own cart UI (drawer or
  notification, whichever the Cart type setting produced).

  Progressive enhancement, deliberately:
  • The forms are real <form action="/cart/add" method="post"> elements. With no
    JS, or if the theme has no <cart-drawer>, this script never calls
    preventDefault and the browser submits normally. Correctness does not
    depend on this file running.
  • Binds once via delegation on document, so including it from several sections
    is harmless (the __cwCartBound guard).

  NEVER re-submits the form after an AJAX attempt. By the time a response is
  parsed the item is already added server-side, so a "retry" would add it twice.
  Any failure path navigates to the cart instead — safe whether or not the add
  landed.
*/
(function () {
  if (window.__cwCartBound) return;
  window.__cwCartBound = true;

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /*
    Use whichever cart UI the merchant's "Cart type" setting produced:
      drawer       -> <cart-drawer>        (rendered by theme.liquid)
      notification -> <cart-notification>  (rendered by the CleanWhey header)
    Both expose the same renderContents(parsedState) contract, and both declare
    the sections they need via getSectionsToRender() — so ask the element rather
    than hardcoding section names, which is what makes this work for either.
  */
  function getCartUI() {
    var el = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    return el && typeof el.renderContents === 'function' ? el : null;
  }

  function sectionsFor(el) {
    try {
      var list = el.getSectionsToRender();
      if (list && list.length) {
        return list
          .map(function (s) {
            return s.id;
          })
          .join(',');
      }
    } catch (err) {
      /* fall through */
    }
    return 'cart-icon-bubble';
  }

  /*
    Re-apply the founder's "View cart" wording.

    Dawn re-renders that link's contents from the cart-notification-button
    section on every add, so a label set server-side in the snippet is wiped as
    soon as something is added. Blank means "leave Dawn's counted label alone".
  */
  function applyLabels() {
    var labels = window.CW_CART_LABELS || {};
    if (!labels.view) return;
    var btn = document.getElementById('cart-notification-button');
    if (btn) btn.textContent = labels.view;
  }

  /*
    Copy the fresh count out of Dawn's hidden #cart-icon-bubble sink into the
    header's own badge. Done this way because that section ships a whole cart
    icon, which would duplicate the header's if rendered visibly.
  */
  function syncCartCount() {
    var sink = document.getElementById('cart-icon-bubble');
    var badge = document.querySelector('[data-cw-cart-count]');
    if (!sink || !badge) return;
    var src = sink.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    var count = src ? src.textContent.trim() : '';
    if (count) {
      badge.textContent = count;
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  /*
    Auto-dismiss the confirmation after AUTO_DISMISS_MS.

    Only the small notification toast — a cart DRAWER is a working surface and
    must never close itself under the shopper.

    The timer pauses while the shopper is engaged with the panel and resumes
    when they disengage: yanking it away mid-reach for "Check out" would be
    worse than leaving it open.
  */
  // Set by the header section from its "Close after" setting; 0 disables
  // auto-dismiss. Falls back to 5s if the header did not publish a value.
  var AUTO_DISMISS_MS = typeof window.CW_CART_DISMISS_MS === 'number' ? window.CW_CART_DISMISS_MS : 5000;

  /*
    What counts as "the shopper is using this panel".

    Deliberately NOT any focusin: Dawn's CartNotification.open() runs
    `this.notification.focus()` on transitionend, so the panel focuses ITSELF
    ~300ms after opening. Treating that as engagement cancelled the countdown
    before it could ever fire, which is why the timer previously appeared to
    start only after a hover — the mouseleave was the sole thing that restarted
    it. Dawn focuses the dialog container (tabindex="-1"); a real keyboard user
    lands on a control, so testing for a control separates the two.
  */
  var INTERACTIVE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function autoDismiss(el) {
    if (!AUTO_DISMISS_MS) return; // 0 = stay open until dismissed
    if (!el || el.tagName.toLowerCase() !== 'cart-notification') return;
    if (typeof el.close !== 'function') return;

    var start = function () {
      clearTimeout(el.__cwTimer);
      // Opened under a resting cursor — treat as engaged; mouseleave restarts.
      if (el.matches && el.matches(':hover')) return;
      el.__cwTimer = setTimeout(function () {
        try {
          el.close();
        } catch (err) {
          /* nothing useful to do — leaving it open is harmless */
        }
      }, AUTO_DISMISS_MS);
    };
    var stop = function () {
      clearTimeout(el.__cwTimer);
    };

    if (!el.__cwHoverBound) {
      el.__cwHoverBound = true;
      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', start);
      el.addEventListener('focusin', function (e) {
        if (e.target && e.target.matches && e.target.matches(INTERACTIVE)) stop();
      });
      el.addEventListener('focusout', function (e) {
        // Resume only once focus has left the panel entirely.
        if (!el.contains(e.relatedTarget)) start();
      });
    }
    start();
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[data-cw-cart-form]')) return;

    var cartUI = getCartUI();
    // No cart UI on the page — let the native submit happen (goes to /cart).
    if (!cartUI) return;

    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    var label = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    var body = new FormData(form);
    // Ask Shopify to return the re-rendered drawer + cart bubble in one round trip.
    body.append('sections', sectionsFor(cartUI));
    body.append('sections_url', window.location.pathname);

    fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        // Shopify returns a `status` field on errors (e.g. sold out).
        if (!result.ok || result.data.status) {
          window.location = cartUrl();
          return;
        }
        if (result.data.sections) {
          cartUI.renderContents(result.data);
          syncCartCount();
          applyLabels();
          autoDismiss(cartUI);
        } else {
          window.location = cartUrl();
        }
      })
      .catch(function () {
        // Do not re-submit — the add may already have succeeded.
        window.location = cartUrl();
      })
      .then(function () {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          if (label) btn.textContent = label;
        }
      });
  });
})();
